import { shortSpinId } from '../../../Model/Spin/spinId.js'
import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useGetAuthenticatedUser } from '../../../Flux/Selector/index.js'
import { isGlobalAdmin } from '../../../Model/User/IUser.js'
import type { DriftTransition } from '../../../Generated/DriftTransition.ts'
import { StatsBarChart } from '../AdminStatsPage/StatsBarChart.js'
import { Card, KpiTile } from '../AdminStatsPage/StatsUi.js'
import { SpinChrome } from './SpinChrome.js'
import { InfoTip } from './SpinUi.js'
import { driftRankData, driftVal, KNOWN_SOURCES, RANK_LABELS, tsLabel } from './spinMetrics.logic.js'
import { useSelectedRun } from './useSelectedRun.js'
import { useSpinDrift } from './useSpinData.js'

export const SpinDriftPage = () => {
  const user = useGetAuthenticatedUser()
  const sel = useSelectedRun()
  const { selected } = sel
  // Filters narrow the transitions table only; the per-rank overview + KPIs always reflect the full run.
  const [rank, setRank] = useState<number | undefined>(undefined)
  const [source, setSource] = useState('')
  const { drift, loading } = useSpinDrift(selected?.id, rank, source || undefined)

  if (!user) return <></>
  if (!isGlobalAdmin(user)) return <Navigate to="/home" replace />

  const transitions = drift?.transitions ?? []

  return (
    <div className="space-y-8">
      <SpinChrome title="SPIN · Drift" sel={sel} />

      {selected && !loading && (!drift || drift.supersedeEdges === 0) && (
        <p className="text-gray-500">No supersede edges to compare for this run — nothing drifted.</p>
      )}
      {selected && drift && drift.supersedeEdges > 0 && (
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">
            Taxonomic drift — {selected.algorithmType} · {tsLabel(selected.startedAt)}
            <InfoTip tip="Among the SPINs THIS run minted from a single predecessor, where the cluster carried forward 1:1 but its resolved taxonomy changed — the biology stayed, the answer moved. Compares each successor's spin_taxonomy against its predecessor's." />
          </h2>
          <p className="text-sm text-gray-500">
            Supersede edges (a SPIN with exactly 1 predecessor — the &ldquo;1:1&rdquo; from Recompute outcomes) whose resolved taxonomy
            moved while the cluster carried forward. Empty values (null / &lsquo;&rsquo; / &lsquo;nan&rsquo;) are normalised before
            comparison, so &ldquo;empty → empty&rdquo; no longer counts as drift.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <KpiTile
              label={
                <>
                  Supersede edges
                  <InfoTip tip="1:1 carry-forwards examined (successor with exactly one predecessor). The denominator for drift." />
                </>
              }
              value={drift.supersedeEdges.toLocaleString()}
            />
            <KpiTile
              label={
                <>
                  Drifted (source×edge)
                  <InfoTip tip="Count of (edge, taxonomy source) pairs where the ladder changed. One edge can drift in several sources, so this can exceed the edge count. Unaffected by the filters below." />
                </>
              }
              value={drift.driftedPairs.toLocaleString()}
            />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card title="Drift by rank (shallowest changed)">
              <StatsBarChart data={driftRankData(drift.perRank ?? [])} />
              <p className="text-xs text-gray-500 mt-2">
                Each drifted edge is counted at the <b>shallowest</b> rank that changed. Shallow drift (phylum/class) is far more alarming
                than species-level reshuffling.
              </p>
            </Card>
            <Card title={`Drift transitions (${transitions.length})`}>
              <div className="flex flex-wrap items-center gap-2 text-sm mb-2">
                <span className="text-gray-500">Filter</span>
                <select
                  className="select select-bordered select-xs"
                  value={rank ?? ''}
                  onChange={(e) => setRank(e.target.value === '' ? undefined : Number(e.target.value))}
                >
                  <option value="">All ranks</option>
                  {RANK_LABELS.map((label, i) => (
                    <option key={label} value={i}>
                      {label}
                    </option>
                  ))}
                </select>
                <select className="select select-bordered select-xs" value={source} onChange={(e) => setSource(e.target.value)}>
                  <option value="">All sources</option>
                  {KNOWN_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              {transitions.length === 0 ? (
                <p className="text-gray-500 text-sm">No taxonomy changes across supersede edges for this filter.</p>
              ) : (
                <div className="overflow-x-auto max-h-[32rem]">
                  <table className="table table-xs table-pin-rows">
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Rank</th>
                        <th>
                          Before → After
                          <InfoTip tip="Predecessor's label → successor's label at the changed rank. ∅ = null/empty/nan (resolution lost or gained)." />
                        </th>
                        <th className="text-right">
                          Edges
                          <InfoTip tip="How many supersede edges show exactly this transition (each edge counted once, at its shallowest changed rank). Distinct transitions are grouped so one recurring story doesn't fill the table." />
                        </th>
                        <th>Example</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transitions.map((t: DriftTransition) => (
                        <tr key={`${t.source}-${t.rank}-${t.before ?? '∅'}-${t.after ?? '∅'}`}>
                          <td>{t.source}</td>
                          <td>{RANK_LABELS[t.rank] ?? t.rank}</td>
                          <td className="font-mono">
                            {driftVal(t.before ?? null)} → {driftVal(t.after ?? null)}
                          </td>
                          <td className="text-right tabular-nums">{t.occurrences.toLocaleString()}</td>
                          <td className="font-mono">
                            <Link to={`/admin/spin/s/${t.exampleSuccessorId}`} className="link link-primary">
                              {shortSpinId(t.exampleSuccessorId)}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </section>
      )}
    </div>
  )
}
